import { type Component, Show, For, createMemo } from 'solid-js';
import { getBrowserInfo, type BrowserInfo } from '../utils/detect-browser';
import styles from './PermissionDenied.module.css';

export type PermissionDenialReason = 'dismissed' | 'blocked' | 'not-found';

export interface PermissionDeniedProps {
  /** Why the permission was denied */
  reason: PermissionDenialReason;
  /** Re-invoke the permission request */
  onTryAgain: () => void;
  /** Go back to landing page */
  onBack: () => void;
}

/** Step-by-step instruction entry */
interface Instruction {
  steps: string[];
}

/**
 * Returns device-specific instructions for re-enabling camera/mic permissions.
 */
function getInstructions(info: BrowserInfo, reason: PermissionDenialReason): Instruction {
  const { browser, os } = info;

  // Not found — same across all browsers
  if (reason === 'not-found') {
    return {
      steps: [
        'Make sure a camera and microphone are connected to your device.',
        'If using an external webcam or mic, unplug and replug it.',
        'Close any other apps that might be using the camera or microphone (Zoom, FaceTime, etc.).',
        'Restart your browser and try again.',
      ],
    };
  }

  // Dismissed — can just retry
  if (reason === 'dismissed') {
    return {
      steps: [
        'When the browser asks for camera and microphone access, click "Allow".',
        'If you don\'t see the prompt, click the "Try Again" button below.',
      ],
    };
  }

  // Blocked — browser + OS specific instructions
  if (browser === 'chrome' && (os === 'windows' || os === 'macos' || os === 'linux')) {
    return {
      steps: [
        'Click the lock or tune icon in the address bar (left of the URL).',
        'Find "Camera" and "Microphone" in the permissions list.',
        'Change both from "Block" to "Allow".',
        'Reload this page or click "Try Again" below.',
      ],
    };
  }

  if (browser === 'chrome' && os === 'android') {
    return {
      steps: [
        'Tap the lock icon in the address bar.',
        'Tap "Permissions" or "Site settings".',
        'Enable "Camera" and "Microphone".',
        'Return to DuoCast and tap "Try Again".',
      ],
    };
  }

  if (browser === 'safari' && os === 'macos') {
    return {
      steps: [
        'Open Safari menu > Settings (or Preferences).',
        'Go to the "Websites" tab.',
        'Select "Camera" in the left sidebar, then find this site and set it to "Allow".',
        'Repeat for "Microphone".',
        'Close Settings and click "Try Again" below.',
      ],
    };
  }

  if (browser === 'safari' && os === 'ios') {
    return {
      steps: [
        'Open the Settings app on your device.',
        'Scroll down and tap "Safari".',
        'Under "Settings for Websites", tap "Camera" and set it to "Allow".',
        'Do the same for "Microphone".',
        'Return to DuoCast in Safari and tap "Try Again".',
      ],
    };
  }

  if (browser === 'firefox' && (os === 'windows' || os === 'macos' || os === 'linux')) {
    return {
      steps: [
        'Click the lock icon in the address bar.',
        'Click "Connection secure" or the permissions section.',
        'Find "Use the Camera" and "Use the Microphone".',
        'Remove the "Blocked" entry for each, then click "Try Again".',
      ],
    };
  }

  if (browser === 'edge' && (os === 'windows' || os === 'macos')) {
    return {
      steps: [
        'Click the lock icon in the address bar.',
        'Find "Camera" and "Microphone" permissions.',
        'Change both from "Block" to "Allow".',
        'Reload the page or click "Try Again" below.',
      ],
    };
  }

  if (browser === 'samsung') {
    return {
      steps: [
        'Tap the menu icon (three lines) at the bottom of Samsung Internet.',
        'Tap "Settings" > "Sites and downloads" > "Site permissions".',
        'Enable "Camera" and "Microphone".',
        'Return to DuoCast and tap "Try Again".',
      ],
    };
  }

  // Generic fallback
  return {
    steps: [
      'Open your browser settings or preferences.',
      'Navigate to "Privacy" or "Site Settings".',
      'Find "Camera" and "Microphone" permissions.',
      'Allow access for this website.',
      'Reload the page and click "Try Again".',
    ],
  };
}

function formatBrowserName(info: BrowserInfo): string {
  const browserNames: Record<string, string> = {
    chrome: 'Chrome',
    safari: 'Safari',
    firefox: 'Firefox',
    edge: 'Edge',
    samsung: 'Samsung Internet',
    unknown: 'Browser',
  };

  const osNames: Record<string, string> = {
    ios: 'iOS',
    android: 'Android',
    macos: 'macOS',
    windows: 'Windows',
    linux: 'Linux',
    unknown: '',
  };

  const b = browserNames[info.browser] ?? 'Browser';
  const o = osNames[info.os] ?? '';
  return o ? `${b} on ${o}` : b;
}

/**
 * PermissionDenied overlay component.
 * Shows device-specific, step-by-step instructions for enabling
 * camera and microphone permissions. Designed as a helpful guide,
 * not an error page.
 */
const PermissionDenied: Component<PermissionDeniedProps> = (props) => {
  const browserInfo = createMemo(() => getBrowserInfo());

  const instructions = createMemo(() =>
    getInstructions(browserInfo(), props.reason)
  );

  const isNotFound = () => props.reason === 'not-found';
  const isDismissed = () => props.reason === 'dismissed';

  const headingText = () => {
    if (isNotFound()) return 'No Camera or Microphone Detected';
    if (isDismissed()) return 'Permission Needed';
    return 'Camera & Mic Access Blocked';
  };

  const subheadingText = () => {
    if (isNotFound()) {
      return 'We could not find a camera or microphone on your device. Please check your setup.';
    }
    if (isDismissed()) {
      return 'DuoCast needs your camera and microphone to connect you with someone. Please allow access when prompted.';
    }
    return 'It looks like permissions are blocked in your browser settings. Follow the steps below to enable them.';
  };

  return (
    <div
      class={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="perm-heading"
      aria-describedby="perm-subheading"
    >
      <div class={styles.card}>
        {/* Icon */}
        <div class={`${styles.iconArea} ${isNotFound() ? styles.iconAreaError : ''}`} aria-hidden="true">
          <Show when={isNotFound()} fallback={<span>&#128275;</span>}>
            <span>&#128247;</span>
          </Show>
        </div>

        {/* Heading */}
        <h2 id="perm-heading" class={styles.heading}>{headingText()}</h2>
        <p id="perm-subheading" class={styles.subheading}>{subheadingText()}</p>

        {/* Detected device badge */}
        <div class={styles.deviceBadgeWrapper}>
          <span class={styles.deviceBadge}>
            Detected: {formatBrowserName(browserInfo())}
          </span>
        </div>

        {/* Not-found hint */}
        <Show when={isNotFound()}>
          <div class={styles.noDeviceHint}>
            <p class={styles.noDeviceHintText}>
              If you are on a desktop without a built-in camera, you will need to connect an external
              webcam and microphone before you can use DuoCast.
            </p>
          </div>
        </Show>

        {/* Step-by-step instructions */}
        <ol class={styles.steps} aria-label="Steps to enable permissions">
          <For each={instructions().steps}>
            {(step, index) => (
              <li class={styles.step}>
                <span class={styles.stepNumber} aria-hidden="true">{index() + 1}</span>
                <span class={styles.stepText}>{step}</span>
              </li>
            )}
          </For>
        </ol>

        {/* Actions */}
        <div class={styles.actions}>
          <Show when={!props.reason || isDismissed() || isNotFound()}>
            <button
              class={`btn btn-primary btn-lg ${styles.tryAgainButton}`}
              onClick={() => props.onTryAgain()}
              type="button"
            >
              Try Again
            </button>
          </Show>

          <Show when={props.reason === 'blocked'}>
            <button
              class={`btn btn-primary btn-lg ${styles.tryAgainButton}`}
              onClick={() => props.onTryAgain()}
              type="button"
            >
              I've Updated Settings — Try Again
            </button>
          </Show>

          <button
            class={styles.backButton}
            onClick={() => props.onBack()}
            type="button"
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionDenied;
